import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanComparisonResult } from '../../../services/planComparisonService';
import PaymentButtons from '../PaymentButtons';

interface BlockedFeaturesStepProps {
  comparison: PlanComparisonResult;
}

export default function BlockedFeaturesStep({ comparison }: BlockedFeaturesStepProps) {
  const { t } = useTranslation();
  const [showPayment, setShowPayment] = useState(false);
  
  // Si está mostrando el panel de pago, renderizar eso
  if (showPayment) {
    return <PaymentButtons onCancel={() => setShowPayment(false)} />;
  }
  
  // Obtener features bloqueadas
  const blockedFeatures = comparison.changes.filter(
    c => c.status === 'blocked' || c.status === 'limited_to_blocked'
  );
  
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="text-6xl mb-4">⏸️</div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('blockedFeatures.title')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('blockedFeatures.subtitle')}
        </p>
      </div>
      
      {/* Lista de features bloqueadas */}
      <div className="space-y-4">
        {blockedFeatures.map((feature, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 border-2 border-gray-200 dark:border-gray-700"
          >
            <div className="flex gap-4">
              <div className="text-4xl flex-shrink-0">
                {feature.feature === 'rewards' ? '🎁' : 
                 feature.feature === 'mealPlans' ? '🍽️' : 
                 feature.feature === 'aiGenerationsPerMonth' ? '🤖' : '🔒'}
              </div>
              
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.featureName}
                </h3>
                
                <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  {feature.feature === 'rewards' && (
                    <>
                      <p className="flex items-start gap-2">
                        <span className="text-green-500 font-bold mt-0.5">✓</span>
                        <span>{t('blockedFeatures.rewards1')}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="text-green-500 font-bold mt-0.5">✓</span>
                        <span>{t('blockedFeatures.rewards2')}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="text-green-500 font-bold mt-0.5">✓</span>
                        <span>{t('blockedFeatures.rewards3')}</span>
                      </p>
                    </>
                  )}
                  
                  {feature.feature === 'mealPlans' && (
                    <>
                      <p className="flex items-start gap-2">
                        <span className="text-green-500 font-bold mt-0.5">✓</span>
                        <span>{t('blockedFeatures.meals1')}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="text-green-500 font-bold mt-0.5">✓</span>
                        <span>{t('blockedFeatures.meals2')}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="text-green-500 font-bold mt-0.5">✓</span>
                        <span>{t('blockedFeatures.meals3')}</span>
                      </p>
                    </>
                  )}
                  
                  {feature.feature === 'aiGenerationsPerMonth' && (
                    <>
                      <p className="flex items-start gap-2">
                        <span className="text-green-500 font-bold mt-0.5">✓</span>
                        <span>{t('blockedFeatures.ai1')}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="text-green-500 font-bold mt-0.5">✓</span>
                        <span>{t('blockedFeatures.ai2')}</span>
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Mensaje de tranquilidad */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border-2 border-blue-200 dark:border-blue-800">
        <div className="flex gap-4">
          <div className="text-3xl flex-shrink-0">💡</div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('blockedFeatures.nothingDeletedTitle')}
            </h3>
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-green-500 font-bold mt-0.5">✓</span>
                <span>{t('blockedFeatures.safe1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 font-bold mt-0.5">✓</span>
                <span>{t('blockedFeatures.safe2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 font-bold mt-0.5">✓</span>
                <span>{t('blockedFeatures.safe3')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Call to action para renovar */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 border-2 border-purple-200 dark:border-purple-800 text-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {t('blockedFeatures.ctaTitle')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('blockedFeatures.ctaDesc')}
        </p>
        <button
          onClick={() => setShowPayment(true)}
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-semibold transition-all"
        >
          💳 {t('blockedFeatures.viewPlans')}
        </button>
      </div>
    </div>
  );
}
